// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The guard against a login ↔ app reload loop. The login page forwards a
 * browser that is already signed in; if that browser keeps coming back (the
 * app's first API call is refused, so it is sent to log in again), each hop
 * waits longer, and after a few it stops forwarding and says why instead of
 * reloading forever.
 *
 * Hops live in sessionStorage (per tab, survives the reloads); ones older
 * than the window are forgotten. Pure logic, unit-tested in Node.
 */

export interface HopStore {
  get(): string | null;
  set(value: string): void;
}

/** Hops counted within this window (ms). */
export const LOGIN_LOOP_WINDOW_MS = 2 * 60 * 1000;
/** The automatic forward stops on this hop. */
export const LOGIN_LOOP_MAX_HOPS = 4;

export interface LoginHop {
  /** Hops within the window, this one included. */
  hops: number;
  /** Wait this long before forwarding (ms): 0, then doubling from 500, up to 4 s. */
  delayMs: number;
  /** Too many: show the form and the reason, do not forward. */
  stop: boolean;
}

function read(store: HopStore, now: number): number[] {
  try {
    const parsed: unknown = JSON.parse(store.get() ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((t): t is number => typeof t === 'number' && now - t >= 0 && now - t < LOGIN_LOOP_WINDOW_MS)
      : [];
  } catch {
    return [];
  }
}

/** Count an automatic forward from the login page at `now`. */
export function recordLoginHop(store: HopStore, now: number): LoginHop {
  const hops = [...read(store, now), now];
  try {
    store.set(JSON.stringify(hops));
  } catch {
    /* no storage: every hop looks like the first; the confirm in the bridge still holds */
  }
  const n = hops.length;
  return {
    hops: n,
    delayMs: n <= 1 ? 0 : Math.min(4000, 500 * 2 ** (n - 2)),
    stop: n >= LOGIN_LOOP_MAX_HOPS,
  };
}

/** Forget the hops (a deliberate sign-in starts over). */
export function clearLoginHops(store: HopStore): void {
  try {
    store.set('[]');
  } catch {
    /* nothing to clear */
  }
}

/** The tab's sessionStorage, or a throwaway one when it is not available. */
export function sessionHopStore(): HopStore {
  const key = 'reusex-login-hops';
  let fallback: string | null = null;
  return {
    get() {
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return fallback;
      }
    },
    set(value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch {
        fallback = value;
      }
    },
  };
}
